import { useEffect, useState } from 'react';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onDismiss: () => void;
  action?: ToastAction;
}

export function Toast({
  message,
  variant = 'info',
  duration = 3000,
  onDismiss,
  action,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onDismiss, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onDismiss]);

  const variantStyles = {
    success: 'bg-status-green text-white shadow-elevated',
    error: 'bg-danger text-white shadow-elevated',
    info: 'bg-white border border-gray-200 text-editorial-black shadow-elevated',
  };

  const iconMap = {
    success: 'check_circle',
    error: 'error',
    info: 'info',
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`mx-auto max-w-md transform transition-all duration-300 motion-reduce:transition-none ${
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0 motion-reduce:translate-y-0'
      } ${variant === 'success' ? 'animate-success-pop' : ''}`}
    >
      <div
        className={`flex items-center gap-2 rounded-lg px-4 py-3 ${variantStyles[variant]}`}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 20 }}
          aria-hidden="true"
        >
          {iconMap[variant]}
        </span>
        <span className="flex-1 text-sm font-sans font-medium">{message}</span>
        {action && (
          <button
            onClick={() => {
              action.onClick();
              setIsVisible(false);
              setTimeout(onDismiss, 300);
            }}
            className="min-h-[44px] min-w-[44px] rounded px-3 py-1 text-sm font-sans font-medium underline opacity-90 transition-opacity hover:opacity-100"
          >
            {action.label}
          </button>
        )}
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onDismiss, 300);
          }}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded p-1 opacity-70 transition-opacity hover:opacity-100"
          aria-label="Dismiss"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">
            close
          </span>
        </button>
      </div>
    </div>
  );
}
